"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AnimatedCounter } from "./animated-counter";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";

function highlightSubcopy(text: string, lang: string) {
  const pattern = lang === "fr" ? /(cofondateur technique)/i : /(technical co-founder)/i;
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    (lang === "fr" ? /cofondateur technique/i : /technical co-founder/i).test(part) ? (
      <span key={i} className="text-gold">
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export const Hero = () => {
  const { t, lang } = useV2Translation();
  const headlineWords = t.hero.headline1.split(" ");

  const wordVariants = {
    hidden: { opacity: 0, y: 32, rotateX: -45 },
    show: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      },
    },
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative grain-bg overflow-hidden"
    >
      {/* Bento grid */}
      <div className="relative max-w-[1500px] mx-auto px-4 lg:px-6 pt-20 lg:pt-20 pb-4 lg:pb-4">
        <div
          className="grid grid-cols-12 gap-3 lg:gap-3"
          style={{
            minHeight: "calc(100svh - 6rem)",
            gridTemplateRows: "auto repeat(6, minmax(0, 1fr))",
          }}
        >
          {/* ── TILE 1: Eyebrow strip ── */}
          <div className="col-span-12 border border-ink/15 bg-paper-pure px-5 py-2.5 flex items-center">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 bg-gold rotate-45 animate-pulse" />
              <span className="eyebrow">{t.hero.eyebrow}</span>
            </div>
          </div>

          {/* ── TILE 2: H1 — large left tile ── */}
          <motion.div
            data-testid="hero-headline-tile"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-8 row-span-4 border border-ink/15 bg-paper-pure relative overflow-hidden p-5 lg:p-8 flex flex-col justify-center"
          >
            {/* Decorative giant outline text in corner */}
            <span
              aria-hidden
              className="absolute -bottom-12 -left-6 font-display italic text-[260px] leading-none text-ink/[0.025] select-none pointer-events-none"
            >
              L&eacute;man
            </span>

            <h1
              data-testid="hero-headline"
              className="font-display font-normal text-[32px] sm:text-5xl lg:text-[48px] xl:text-[58px] leading-[1.04] tracking-tight relative"
              style={{ perspective: 1000 }}
            >
              <motion.span
                initial="hidden"
                animate="show"
                transition={{ staggerChildren: 0.06, delayChildren: 0.1 }}
                className="inline"
              >
                {headlineWords.map((w, i) => {
                  const clean = w.replace(/[^a-zA-Z]/g, "");
                  const isBusiness = /business/i.test(clean);
                  const isCode = /code/i.test(clean);
                  return (
                    <React.Fragment key={i}>
                      <motion.span
                        variants={wordVariants}
                        className={`inline-block mr-[0.25em] ${isBusiness ? "text-gold italic" : ""} ${isCode ? "italic" : ""}`}
                        style={{ transformOrigin: "50% 100%" }}
                      >
                        {w}
                      </motion.span>
                      {(i === 2 || i === 6) && <br className="hidden sm:block" />}
                    </React.Fragment>
                  );
                })}
              </motion.span>
            </h1>
          </motion.div>

          {/* ── TILE 3: Stat 15+ — navy ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="hero-stat-0"
            className="col-span-6 lg:col-span-4 row-span-2 bg-navy text-paper p-6 lg:p-8 relative overflow-hidden flex flex-col justify-between"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/55">
              {t.hero.stat1Title}
            </span>

            <div className="relative flex items-end gap-2">
              <span
                className="font-display leading-none tracking-tight text-[80px] lg:text-[112px] xl:text-[128px]"
                aria-label="Fifteen plus"
              >
                <AnimatedCounter to={15} />
              </span>
              <span className="font-display italic text-gold text-5xl lg:text-7xl leading-none mb-2 lg:mb-3">
                {t.hero.stat1Suffix}
              </span>
            </div>

            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper/75 max-w-[22ch] mt-2">
              {t.hero.stat1Label}
            </p>
          </motion.div>

          {/* ── TILE 4: 100% ownership — gold ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            data-testid="hero-pattern-tile"
            className="col-span-6 lg:col-span-4 row-span-2 bg-gold text-navy-deep relative overflow-hidden diagonal-lines p-5 lg:p-6 flex flex-col justify-between"
          >
            <div className="flex items-center justify-between relative z-10">
              <span className="font-mono text-[10px] uppercase tracking-widest text-navy-deep/80">
                {t.hero.stat2Label}
              </span>
              <span className="inline-block w-2 h-2 bg-navy-deep rotate-45" />
            </div>

            <svg
              viewBox="0 0 200 200"
              className="absolute inset-0 w-full h-full opacity-[0.18] pointer-events-none"
              aria-hidden
            >
              <motion.g
                style={{ transformOrigin: "100px 100px" }}
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              >
                <rect
                  x="40"
                  y="40"
                  width="120"
                  height="120"
                  stroke="#0D1B2E"
                  strokeWidth="1"
                  fill="none"
                />
                <rect
                  x="55"
                  y="55"
                  width="90"
                  height="90"
                  stroke="#0D1B2E"
                  strokeWidth="1"
                  fill="none"
                />
                <rect
                  x="70"
                  y="70"
                  width="60"
                  height="60"
                  stroke="#0D1B2E"
                  strokeWidth="1"
                  fill="none"
                />
              </motion.g>
            </svg>

            <div className="relative z-10">
              <p className="font-display text-3xl lg:text-5xl leading-[0.95] italic">
                {t.hero.stat2Value}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-navy-deep/75 mt-3">
                {t.hero.stat2Sub}
              </p>
            </div>
          </motion.div>

          {/* ── TILE 5: Subcopy + CTA + microcopy — large lower-left ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="col-span-12 lg:col-span-8 row-span-2 border border-ink/15 bg-paper-alt p-5 lg:p-6 relative overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full items-center">
              <div className="lg:col-span-7">
                <p
                  data-testid="hero-subcopy"
                  className="font-body text-sm lg:text-base text-ink/90 leading-relaxed max-w-[52ch]"
                >
                  {highlightSubcopy(t.hero.subcopy, lang)}
                </p>
                <p className="font-mono text-[10px] text-[#6b6860] mt-2 max-w-[52ch]">
                  {t.hero.microcopy}
                </p>
              </div>

              <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-3">
                <motion.a
                  href="#booking"
                  data-testid="hero-cta-primary"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn-primary group inline-flex items-center justify-between gap-3 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.18em] w-full lg:w-auto lg:min-w-[280px]"
                >
                  <span>{t.hero.ctaPrimary}</span>
                  <motion.span
                    whileHover={{ x: 4, y: -4 }}
                    className="inline-flex"
                  >
                    <ArrowUpRight size={16} strokeWidth={2.2} />
                  </motion.span>
                </motion.a>

                <div className="flex items-center gap-2 self-end">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gold">
                    {t.hero.ctaSecondary}
                  </span>
                </div>
              </div>
            </div>

            {/* Rotated sticker badge breaking the seam */}
            <div
              className="hidden md:flex absolute -top-5 right-8 z-20 bg-navy text-paper px-4 py-2 items-center gap-2"
              style={{ transform: "rotate(-6deg)" }}
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-gold">
                &#9733; &#9733; &#9733; &#9733; &#9733;
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest">
                Founder-rated
              </span>
            </div>
          </motion.div>

          {/* ── TILE 6: 00:00 migration — bottom-right small ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            data-testid="hero-stat-2"
            className="col-span-12 lg:col-span-4 row-span-2 border border-ink/15 bg-paper-pure p-5 lg:p-6 relative overflow-hidden flex flex-col justify-between"
          >
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#6b6860]">
              Migration
            </span>

            <div className="flex items-end justify-between gap-3">
              <div>
                <span className="font-mono text-4xl lg:text-6xl text-ink leading-[0.9]">
                  {t.hero.stat3Value}
                </span>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#6b6860] mt-3 max-w-[20ch]">
                  {t.hero.stat3Label}
                </p>
              </div>
              <span className="hidden lg:inline-flex w-2 h-2 bg-gold rounded-full animate-pulse mb-2" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Ticker */}
      <div className="border-y border-ink/15 bg-paper-alt/60 overflow-hidden relative z-10">
        <div className="ticker-track flex whitespace-nowrap py-3">
          {[...t.hero.tickerItems, ...t.hero.tickerItems].map((item, i) => (
            <span
              key={i}
              className="font-mono text-xs uppercase tracking-[0.22em] px-8 flex items-center gap-8"
            >
              <span className="inline-block w-1.5 h-1.5 bg-gold rotate-45" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
